/**
 * @fileoverview Bulk-edit dialog for time entries (MOR-906 §7.4).
 *
 * Mirrors the precedent library's bulk editor: one gated section per
 * dimension, so an approver updates only the fields they tick. Backed by
 * `timesheets.entries.bulkEditEntries`, which skips entries that can't be
 * edited (wrong status, already written off) and reports how many changed.
 *
 * Deliberately narrow: billable, category and project code are the only
 * dimensions safe to set uniformly across a selection. Units and narrative
 * stay per-entry — see the mutation's note.
 */

import { useEffect, useState } from "react";
import { useMutation } from "convex/react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";

import { extractErrorMessage } from "@/utils/errors";
import { Button } from "@/ui/primitives";
import { Checkbox } from "@/ui/primitives/checkbox";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/ui/composed/overlays/dialog";

export interface BulkEditCategory {
  _id: Id<"timeCategories">;
  name: string;
}

/** One gated section: a checkbox that reveals the field's control. */
function Section({
  id,
  label,
  enabled,
  onEnabledChange,
  children,
}: {
  id: string;
  label: string;
  enabled: boolean;
  onEnabledChange: (enabled: boolean) => void;
  children: React.ReactNode;
}) {
  return (
    <div className="flex flex-col gap-2 rounded-lg border p-3">
      <div className="flex items-center gap-2">
        <Checkbox
          id={id}
          checked={enabled}
          onCheckedChange={(v) => onEnabledChange(v === true)}
        />
        <Label htmlFor={id} className="text-sm font-medium">
          {label}
        </Label>
      </div>
      {enabled && <div className="pl-6">{children}</div>}
    </div>
  );
}

export function BulkEditEntriesDialog({
  open,
  onOpenChange,
  entryIds,
  categories,
  onApplied,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  entryIds: Id<"timeEntries">[];
  categories: BulkEditCategory[];
  onApplied: () => void;
}) {
  const bulkEditEntries = useMutation(api.timesheets.entries.bulkEditEntries);

  const [updateBillable, setUpdateBillable] = useState(false);
  const [billable, setBillable] = useState<"billable" | "non-billable">(
    "billable",
  );
  const [updateCategory, setUpdateCategory] = useState(false);
  const [categoryId, setCategoryId] = useState<string>("");
  const [updateProjectCode, setUpdateProjectCode] = useState(false);
  const [projectCode, setProjectCode] = useState<"pre" | "post">("post");
  const [busy, setBusy] = useState(false);

  // Reset on close so the next open starts clean.
  useEffect(() => {
    if (!open) {
      setUpdateBillable(false);
      setUpdateCategory(false);
      setUpdateProjectCode(false);
      setCategoryId("");
      setBillable("billable");
      setProjectCode("post");
    }
  }, [open]);

  const nothingChosen =
    !updateBillable && !updateCategory && !updateProjectCode;
  // A ticked category section with no category picked can't be applied.
  const categoryIncomplete = updateCategory && categoryId === "";
  const count = entryIds.length;

  const handleApply = async () => {
    setBusy(true);
    try {
      const updated = await bulkEditEntries({
        entryIds,
        patch: {
          ...(updateBillable ? { billable: billable === "billable" } : {}),
          ...(updateCategory
            ? { categoryId: categoryId as Id<"timeCategories"> }
            : {}),
          ...(updateProjectCode ? { projectCode } : {}),
        },
      });
      // The mutation skips entries it can't touch, so the count it returns is
      // the honest figure — surface it rather than the selection size.
      if (updated === 0) {
        toast.info("No entries changed — they already had these values.");
      } else {
        toast.success(
          updated === 1 ? "1 entry updated." : `${updated} entries updated.`,
        );
      }
      if (updated < count) {
        const skipped = count - updated;
        toast.info(
          `${skipped} of ${count} ${count === 1 ? "entry" : "entries"} ${skipped === 1 ? "was" : "were"} unchanged or not editable.`,
        );
      }
      onApplied();
      onOpenChange(false);
    } catch (err) {
      toast.error(extractErrorMessage(err, "Could not update the entries."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            Edit {count} {count === 1 ? "entry" : "entries"}
          </DialogTitle>
          <DialogDescription>
            Select what to update. Entries that are written off, or not
            awaiting/past approval, are skipped.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <Section
            id="bulk-billable"
            label="Billable"
            enabled={updateBillable}
            onEnabledChange={setUpdateBillable}
          >
            <Select
              value={billable}
              onValueChange={(v) =>
                setBillable(v as "billable" | "non-billable")
              }
            >
              <SelectTrigger className="h-8 w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="billable">Billable</SelectItem>
                <SelectItem value="non-billable">Non-billable</SelectItem>
              </SelectContent>
            </Select>
          </Section>

          <Section
            id="bulk-category"
            label="Category"
            enabled={updateCategory}
            onEnabledChange={setUpdateCategory}
          >
            <Select value={categoryId} onValueChange={setCategoryId}>
              <SelectTrigger className="h-8 w-64">
                <SelectValue placeholder="Choose a category…" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((c) => (
                  <SelectItem key={c._id as string} value={c._id as string}>
                    {c.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Section>

          <Section
            id="bulk-project-code"
            label="Project code"
            enabled={updateProjectCode}
            onEnabledChange={setUpdateProjectCode}
          >
            <Select
              value={projectCode}
              onValueChange={(v) => setProjectCode(v as "pre" | "post")}
            >
              <SelectTrigger className="h-8 w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="post">Post-appointment</SelectItem>
                <SelectItem value="pre">Pre-appointment</SelectItem>
              </SelectContent>
            </Select>
          </Section>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            disabled={nothingChosen || categoryIncomplete || busy}
            onClick={() => void handleApply()}
          >
            {busy && <Loader2 className="size-4 animate-spin" />}
            Apply to {count} {count === 1 ? "entry" : "entries"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
